import { toast } from '@systemeio/ui'
import { useFetch } from 'shared/hooks/use-fetch'
import { RequestMethodsEnum } from 'shared/hooks/use-fetch/types'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useOAuthLogin } from 'shared/hooks/use-oauth-login'
import {
  getGoogleAuthUrlPath,
  getGoogleIntegrationSendCode,
  getGoogleReconnectAuthUrlPath,
} from '../api'
import { AuthUrlInterface, IntegrationInterface, IntegrationUseCaseEnum } from '../types'

export const useIntegrateGoogle = ({
  onSuccess,
  onError,
}: {
  onSuccess: (_: IntegrationInterface) => void
  onError?: (_: Error) => void
}) => {
  const { t } = useLocoTranslation()
  const { fetcher: fetchAuthPath, isFetching } = useFetch<RequestMethodsEnum.get, AuthUrlInterface>(
    {
      method: RequestMethodsEnum.get,
    },
  )
  const { fetcher: sendCode } = useFetch<RequestMethodsEnum.post, IntegrationInterface>({
    method: RequestMethodsEnum.post,
  })

  const onErrorHandler = onError ?? handleIntegrationError

  const { openAuthWindow } = useOAuthLogin()

  const subscribeOnMessageFromCallbackPage = (authWindow: WindowProxy) => {
    const handleMessage = async (event: MessageEvent<{ code: string; error: string }>) => {
      if (event.origin !== window.location.origin) return

      const { code, error } = event.data

      if (event.source !== authWindow) return
      if (code) {
        try {
          const integrationItem = await sendCode(getGoogleIntegrationSendCode(code))
          onSuccess(integrationItem)
        } catch (e) {
          onErrorHandler(e as unknown as Error)
        }
      } else if (error) {
        onErrorHandler(new Error(error))
      }

      window.removeEventListener('message', handleMessage)
    }

    window.addEventListener('message', handleMessage)
  }

  const openGoogleLogin = async (useCase: IntegrationUseCaseEnum, preselectedEmail?: string) => {
    try {
      const authUrl = await fetchAuthPath(getGoogleAuthUrlPath(useCase))

      const url = authUrl.redirectUrl + (preselectedEmail ? `&login_hint=${preselectedEmail}` : '')

      await openAuthWindow({
        locationHref: url,
        subscribeOnMessageFromCallbackPage,
        onError: onErrorHandler,
      })
    } catch (e) {
      onErrorHandler(e as unknown as Error)
    }
  }

  const openGoogleLoginToReconnect = async (
    id: IntegrationInterface['id'],
    preselectedEmail?: string,
  ) => {
    try {
      const authUrl = await fetchAuthPath(getGoogleReconnectAuthUrlPath(id))

      const url = authUrl.redirectUrl + (preselectedEmail ? `&login_hint=${preselectedEmail}` : '')

      await openAuthWindow({
        locationHref: url,
        subscribeOnMessageFromCallbackPage,
        onError: onErrorHandler,
      })
    } catch (e) {
      onErrorHandler(e as unknown as Error)
    }
  }

  async function handleIntegrationError(error: Error) {
    let errorMessage

    switch (error.message) {
      case 'access_denied':
        errorMessage = t(
          'dashboard.user_settings.integrations_page.access_denied_error_description',
        )
        break
      case 'invalid_request':
        errorMessage = t(
          'dashboard.user_settings.integrations_page.invalid_request_error_description',
        )
        break
      case 'unauthorized_client':
        errorMessage = t(
          'dashboard.user_settings.integrations_page.unauthorized_client_error_description',
        )
        break
      case 'unsupported_response_type':
        errorMessage = t(
          'dashboard.user_settings.integrations_page.unsupported_response_type_error_description',
        )
        break
      case 'invalid_scope':
        errorMessage = t(
          'dashboard.user_settings.integrations_page.invalid_scope_error_description',
        )
        break
      case 'server_error':
        errorMessage = t('dashboard.user_settings.integrations_page.server_error_description')
        break
      case 'temporarily_unavailable':
        errorMessage = t(
          'dashboard.user_settings.integrations_page.temporarily_unavailable_error_description',
        )
        break
      default:
        errorMessage =
          error.message || t('dashboard.user_settings.integrations_page.common_error_description')
        break
    }

    toast.error(errorMessage)
  }

  return {
    openGoogleLogin,
    openGoogleLoginToReconnect,
    isFetching,
  }
}
